import GaugeChart from '@/components/layout/GaugeChart';
import Skeleton from '../../components/layout/Skeleton';
import { useQuery } from 'react-query';
import axios from 'axios';
import HBox from '../../components/layout/HBox';
import VBox from '@/components/layout/VBox';
import { translateFilter } from './util';
import { useCallback, useEffect, useState } from 'react';
import Botao from '../../components/Botao';
import { differenceInDays } from 'date-fns';
import {
    Area,
    AreaChart,
    Bar,
    BarChart,
    CartesianGrid,
    Legend,
    Line,
    LineChart,
    ResponsiveContainer,
    Tooltip,
    XAxis,
    YAxis,
} from 'recharts';

const parseDate = (v: string) =>
    new Date(v.substring(0, 10) + 'T00:00:00.000').toLocaleDateString();

export const GraficoSentimentoCliente = ({ colorPalette, periodo, filterCanal }: any) => {
    const [foco, setFoco] = useState<string | null>(null);

    const calcBestInterval = () => {
        let d1 = periodo![1],
            d2 = periodo![2];
        if (!d2) {
            d2 = new Date().toISOString().substring(0, 10);
        }

        const days = differenceInDays(d2, d1);
        if (days <= 7) {
            return 'dia';
        } else if (days <= 31) {
            return 'semana';
        } else {
            return 'mes';
        }
    };

    const [selectedPeriod, setSelectedPeriod] = useState<'dia' | 'semana' | 'mes'>(
        calcBestInterval
    );

    useEffect(() => {
        setSelectedPeriod(calcBestInterval());
    }, [periodo]);

    const CustomTooltip = useCallback(
        ({ active, payload, label, separator }: any) => {
            if (active && payload && payload.length) {
                // Calcula o total para converter em percentual
                const total = payload.reduce((sum: number, p: any) => sum + (p.value || 0), 0);

                const selectedMetric = payload.find((p: any) => foco == p.dataKey);

                return (
                    <div
                        style={{
                            backgroundColor: '#fff8',
                            border: '1px solid #ccc',
                            padding: '0.5rem',
                        }}
                    >
                        {payload?.[0]?.payload?.periodo && (
                            <p>
                                <b>{parseDate(payload[0].payload.periodo)}</b>
                            </p>
                        )}

                        {selectedMetric ? (
                            <>
                                <p>
                                    {selectedMetric.name}
                                    {separator}
                                    {total > 0 ? ((selectedMetric.value / total) * 100).toFixed(1) : '0.0'}%
                                </p>
                            </>
                        ) : (
                            payload.map((p: any) => (
                                <p key={p.dataKey}>
                                    {p.name}
                                    {separator}
                                    {total > 0 ? ((p.value / total) * 100).toFixed(1) : '0.0'}%
                                </p>
                            ))
                        )}
                    </div>
                );
            }

            return null;
        },
        [foco]
    );

    const renderDot = ({
        cx,
        cy,
        fill,
        stroke,
        strokeWidth,
        strokeOpacity,
        dataKey,
    }: any) => {
        const r = 7;
        return (
            <svg
                x={cx - r * 4}
                y={cy - r}
                width={r * 8}
                height={r * 2}
                viewBox={`0 0 ${r * 8} ${r * 2}`}
                onMouseEnter={() => setFoco(dataKey)}
                onMouseLeave={() => setFoco(null)}
            >
                <rect x={0} y={0} width={r * 8} height={r * 2} fill="transparent" />
                <circle
                    cx={r * 4}
                    cy={r}
                    r={r}
                    fill={fill}
                    stroke={stroke}
                    strokeWidth={strokeWidth}
                    opacity={!foco || dataKey === foco ? strokeOpacity : 0.2}
                />
            </svg>
        );
    };

    const q = useQuery(
        ['sentimento_cliente', selectedPeriod, periodo, filterCanal],
        async () => {
            const qs = new URLSearchParams();
            qs.append('facts', 'total');
            qs.append(
                'dimensions',
                `data_criacao_${selectedPeriod},sentimento_cliente`
            );
            qs.append('filter', translateFilter({ periodo, canal_id: filterCanal }));
            const res = await axios.get('/api/relatorios/chat_resumo?' + qs.toString());
            return res.data.result;
        },
        { refetchOnWindowFocus: false }
    );

    const dadosAbsolutos = q.data
        ? q.data.reduce((acc: any[], row: any) => {
            const periodo = row[`data_criacao_${selectedPeriod}`].substring(0, 10);
            if (!acc[acc.length - 1] || acc[acc.length - 1].periodo != periodo)
                acc.push({ periodo, bom: 0, normal: 0, ruim: 0 });
            const curr = acc[acc.length - 1];

            const sentimento = row.sentimento_cliente;
            if (sentimento === 'bom') curr.bom = parseInt(row.total);
            else if (sentimento === 'normal') curr.normal = parseInt(row.total);
            else if (sentimento === 'ruim') curr.ruim = parseInt(row.total);

            return acc;
        }, [])
        : [];

    // Converte para percentuais (não precisa fazer cálculo pois stackOffset="expand" já normaliza)
    const dados = dadosAbsolutos;

    const handleMouseEnter = (o: any) => {
        const { dataKey } = o;
        setFoco(dataKey);
    };

    const handleMouseLeave = () => {
        setFoco(null);
    };

    if (q.isFetching) return <Skeleton format="box" />;

    // Calcula total de chats e score para verificar quantidade mínima
    let totalChats = 0;
    let score = 0;
    dadosAbsolutos.forEach((d: any) => {
        const bom = d.bom || 0;
        const normal = d.normal || 0;
        const ruim = d.ruim || 0;
        totalChats += bom + normal + ruim;
        score += 100 * bom + 50 * normal + 0 * ruim;
    });
    const avg_score = totalChats ? score / totalChats : null;

    if (totalChats < 10) {
        return (
            <VBox center>
                <p
                    style={{
                        fontSize: '0.875rem',
                        color: 'var(--tc-color-gray-500)',
                        textAlign: 'center',
                    }}
                >
                    Sem dados suficientes para o período selecionado.
                </p>
            </VBox>
        );
    }

    return (
        <VBox>
            <HBox>
                <Botao
                    variant={
                        selectedPeriod === 'dia' ? 'outline-primary' : 'outline-secondary'
                    }
                    onClick={() => setSelectedPeriod('dia')}
                >
                    Dia
                </Botao>
                <Botao
                    variant={
                        selectedPeriod === 'semana'
                            ? 'outline-primary'
                            : 'outline-secondary'
                    }
                    onClick={() => setSelectedPeriod('semana')}
                >
                    Semana
                </Botao>
                <Botao
                    variant={
                        selectedPeriod === 'mes' ? 'outline-primary' : 'outline-secondary'
                    }
                    onClick={() => setSelectedPeriod('mes')}
                >
                    Mês
                </Botao>
            </HBox>

            {dados.length === 0 && (
                <VBox center>
                    <p>Nenhum dado encontrado</p>
                </VBox>
            )}

            {dados.length === 1 && (
                <VBox center>
                    <GaugeChart
                        value={avg_score ?? 0}
                        thresholds={[
                            {
                                label: 'Pode melhorar!',
                                value: 33,
                                color: 'var(--tc-color-danger)',
                            },
                            {
                                label: 'Normal',
                                value: 67,
                                color: 'var(--tc-color-info)',
                            },
                            {
                                label: 'Bom!',
                                value: 100,
                                color: 'var(--tc-color-success)',
                            },
                        ]}
                    />
                    <p
                        style={{
                            fontSize: '0.75rem',
                            fontStyle: 'italic',
                            color: 'var(--tc-color-gray-500)',
                            textAlign: 'center',
                        }}
                    >
                        Análise subjetiva calculada por IA. Consulte as conversas do
                        período para mais detalhes.
                    </p>
                </VBox>
            )}

            {dados.length > 1 && (
                <>
                    <ResponsiveContainer width="100%" height={250}>
                        <AreaChart
                            margin={{
                                top: 5,
                                right: 5,
                                left: 5,
                                bottom: 5,
                            }}
                            data={dados}
                            stackOffset="expand"
                        >
                            <CartesianGrid strokeDasharray="3 3" />
                            <XAxis dataKey="periodo" tickFormatter={parseDate} />
                            <YAxis
                                tickFormatter={(value) => `${(value * 100).toFixed(0)}%`}
                                domain={[0, 1]}
                            />
                            <Tooltip
                                cursor={{ fill: '#8882' }}
                                itemSorter={(item: any) => {
                                    return -item.value as number;
                                }}
                                itemStyle={{ padding: 0, fontSize: '0.9rem' }}
                                labelStyle={{ fontWeight: 'bold' }}
                                content={<CustomTooltip />}
                            />
                            <Legend
                                onMouseEnter={handleMouseEnter}
                                onMouseLeave={handleMouseLeave}
                                wrapperStyle={{
                                    height: '60px',
                                    overflowY: 'auto',
                                }}
                                height={80}
                                formatter={(v, e: any, i) => {
                                    const { dataKey } = e;

                                    return (
                                        <span
                                            style={{
                                                color: colorPalette[i % colorPalette.length],
                                                opacity: !foco || foco === dataKey ? 1 : 0.2,
                                                fontWeight:
                                                    foco && foco === dataKey
                                                        ? 'bold'
                                                        : 'normal',
                                            }}
                                        >
                                            {v}
                                        </span>
                                    );
                                }}
                            />

                            <Area
                                type="monotone"
                                dataKey="ruim"
                                name="Ruim"
                                stackId="1"
                                stroke="var(--tc-color-danger)"
                                fill="var(--tc-color-danger)"
                                fillOpacity={!foco || foco === 'ruim' ? 0.6 : 0.2}
                                strokeOpacity={!foco || foco === 'ruim' ? 1 : 0.2}
                            />
                            <Area
                                type="monotone"
                                dataKey="normal"
                                name="Normal"
                                stackId="1"
                                stroke="var(--tc-color-info)"
                                fill="var(--tc-color-info)"
                                fillOpacity={!foco || foco === 'normal' ? 0.6 : 0.2}
                                strokeOpacity={!foco || foco === 'normal' ? 1 : 0.2}
                            />
                            <Area
                                type="monotone"
                                dataKey="bom"
                                name="Bom"
                                stackId="1"
                                stroke="var(--tc-color-success)"
                                fill="var(--tc-color-success)"
                                fillOpacity={!foco || foco === 'bom' ? 0.6 : 0.2}
                                strokeOpacity={!foco || foco === 'bom' ? 1 : 0.2}
                            />
                        </AreaChart>
                    </ResponsiveContainer>
                    <p
                        style={{
                            fontSize: '0.75rem',
                            fontStyle: 'italic',
                            color: 'var(--tc-color-gray-500)',
                            textAlign: 'center',
                        }}
                    >
                        Análise subjetiva calculada por IA. Consulte as conversas do
                        período para mais detalhes.
                    </p>
                </>
            )}
        </VBox>
    );
};
